'use client';
import React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import {
  BellRing,
  KeyRound,
  LayoutDashboard,
  Server,
  Phone,
  PhoneMissed,
  Plug,
  Radio,
  BarChart2,
  BookOpen,
  Building2,
  ChevronLeft,
  ChevronRight,
  LogOut,
  ShieldCheck,
  Users,
} from 'lucide-react';
import { useAuth } from 'react-oidc-context';
import AppLogo from './ui/AppLogo';
import { canAdminister, primaryRole, useMe } from '@/lib/auth/me';
import { grantRole } from '@/lib/dict';

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

const navItems: {
  href: string;
  icon: typeof LayoutDashboard;
  label: string;
  badge: string | null;
  adminOnly?: boolean;
  staffOnly?: boolean;
}[] = [
  { href: '/dashboard', icon: LayoutDashboard, label: 'Дашборд', badge: null },
  { href: '/call-list', icon: Phone, label: 'Звонки', badge: null },
  { href: '/missed-calls', icon: PhoneMissed, label: 'Пропущенные', badge: null },
  { href: '/contacts', icon: Users, label: 'Клиенты', badge: null },
  { href: '/reports', icon: BarChart2, label: 'Отчёты', badge: null },
  { href: '/directories', icon: BookOpen, label: 'Справочники', badge: null },
  { href: '/quality-rules', icon: ShieldCheck, label: 'Настройка разбора', badge: null },
  // Управление доступом видно только администраторам — фильтруется ниже.
  // Не adminOnly: подписку на оповещения настраивает каждый, кому они
  // адресованы, — супервизор в том числе, а он не администратор.
  { href: '/notifications', icon: BellRing, label: 'Оповещения', badge: null },
  // Коллтрекинг видят все: состав пула объясняет, откуда пришёл звонок, и
  // прятать его от супервизора незачем. Править внутри может администратор.
  { href: '/calltracking', icon: Radio, label: 'Коллтрекинг', badge: null },
  // Интеграция отправляет содержание разговоров наружу: раздел ведёт только
  // администратор, остальным его не видно вовсе.
  { href: '/integrations', icon: Plug, label: 'Интеграции', badge: null, adminOnly: true },
  { href: '/access', icon: KeyRound, label: 'Доступ', badge: null, adminOnly: true },
  // Станции обслуживают несколько клиентов сразу и клиенту не принадлежат:
  // раздел прячется по isPlatformStaff, а не по роли.
  // Прячем по `isPlatformStaff`, а не по ответу сервера: вся ветка
  // /api/admin отвечает 404, и существование этих экранов — тоже сведения.
  { href: '/platform', icon: Building2, label: 'Клиенты платформы', badge: null, staffOnly: true },
  { href: '/servers', icon: Server, label: 'Станции', badge: null, staffOnly: true },
];

/** Подпись для плитки тенанта: по первым буквам слов названия. */
function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('');
}

export default function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const pathname = usePathname();
  const me = useMe();
  const auth = useAuth();

  const role = primaryRole(me);
  const tenantInitials = initialsOf(me.tenant.name);

  return (
    <aside
      className="fixed left-0 top-0 h-full bg-white border-r border-slate-200 flex flex-col z-40 transition-all duration-300 overflow-hidden"
      style={{ width: collapsed ? '64px' : '240px' }}
    >
      {/* Logo */}
      <div className="flex items-center justify-between px-4 py-4 border-b border-slate-100 min-h-[56px]">
        {!collapsed && (
          <div className="flex items-center gap-2">
            <AppLogo size={28} />
            <span className="font-bold text-brand-accent text-base tracking-tight">callog.kz</span>
          </div>
        )}
        {collapsed && (
          <div className="mx-auto">
            <AppLogo size={28} />
          </div>
        )}
        {!collapsed && (
          <button
            onClick={onToggle}
            className="p-1 rounded-md hover:bg-slate-100 text-slate-400 transition-colors"
            aria-label="Свернуть меню"
          >
            <ChevronLeft size={16} />
          </button>
        )}
      </div>

      {/* Org switcher */}
      {!collapsed && (
        <div className="px-3 py-3 border-b border-slate-100">
          <div className="w-full flex items-center gap-2 px-3 py-2 rounded-lg">
            <div className="w-7 h-7 rounded-md bg-brand text-on-fill text-xs font-bold flex items-center justify-center flex-shrink-0">
              {tenantInitials}
            </div>
            <div className="flex-1 text-left min-w-0">
              <div className="text-xs font-semibold text-slate-800 truncate" title={me.tenant.name}>
                {me.tenant.name}
              </div>
              <div className="text-[10px] text-slate-400 font-mono">{me.tenant.code}</div>
            </div>
          </div>
        </div>
      )}
      {collapsed && (
        <div className="flex justify-center py-3 border-b border-slate-100">
          <div
            className="w-7 h-7 rounded-md bg-brand text-on-fill text-xs font-bold flex items-center justify-center"
            title={me.tenant.name}
          >
            {tenantInitials}
          </div>
        </div>
      )}

      {/* Nav */}
      <nav className="flex-1 px-2 py-3 space-y-0.5 overflow-y-auto">
        {!collapsed && (
          <div className="px-3 mb-2">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">
              Навигация
            </span>
          </div>
        )}
        {navItems
          .filter((item) => !item.adminOnly || canAdminister(me))
          .filter((item) => !item.staffOnly || me.isPlatformStaff)
          .map(({ href, icon: Icon, label, badge }) => {
            const isActive = pathname === href || pathname.startsWith(href + '/');
            return (
              <Link
                key={href}
                href={href}
                title={collapsed ? label : undefined}
                className={`sidebar-nav-item ${isActive ? 'active' : ''} ${collapsed ? 'justify-center px-0' : ''}`}
              >
                <Icon size={18} className="flex-shrink-0" />
                {!collapsed && (
                  <>
                    <span className="flex-1">{label}</span>
                    {badge && (
                      <span className="ml-auto bg-brand-light text-on-fill text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                        {badge}
                      </span>
                    )}
                  </>
                )}
              </Link>
            );
          })}
      </nav>

      {/* Expand button when collapsed */}
      {collapsed && (
        <div className="flex justify-center py-2 border-t border-slate-100">
          <button
            onClick={onToggle}
            className="p-1.5 rounded-md hover:bg-slate-100 text-slate-400 transition-colors"
            aria-label="Развернуть меню"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* User */}
      <div className={`border-t border-slate-100 p-3 ${collapsed ? 'flex justify-center' : ''}`}>
        {!collapsed ? (
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-light to-brand text-on-fill text-xs font-bold flex items-center justify-center flex-shrink-0">
              {me.initials}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold text-slate-800 truncate" title={me.email}>
                {me.name}
              </div>
              <div className="text-[10px] text-slate-400 truncate">
                {role ? grantRole[role].label : 'Без роли'}
              </div>
            </div>
            <button
              onClick={() => void auth.signoutRedirect()}
              className="p-1 rounded hover:bg-slate-100 text-slate-400"
              title="Выйти"
            >
              <LogOut size={14} />
            </button>
          </div>
        ) : (
          <button
            onClick={() => void auth.signoutRedirect()}
            className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-light to-brand text-on-fill text-xs font-bold flex items-center justify-center"
            title={`${me.name} — выйти`}
          >
            {me.initials}
          </button>
        )}
      </div>
    </aside>
  );
}
